import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Publish the top bar's real height as `--topbar-height` (decision R3).
 *
 * A screen that pins its own frame (`.screen--fixed-frame`) has to know where
 * the bar above it ends, and **the number cannot be written down**. It was, at
 * first — 56px of content plus a 3px rule and a 1px border, which reads like
 * arithmetic and was wrong: the utility cluster is taller than its own
 * `min-height`, so the bar is 63.33px and the register overflowed the viewport
 * by three pixels. Three pixels is enough for a permanent scrollbar and a frame
 * that is not, in fact, fixed.
 *
 * Worse, no constant could have been right. The bar **wraps** on a narrow
 * screen — five sections plus the utility cluster — so its height is a function
 * of the viewport, and a shop on a 1280-wide till and one on a laptop would
 * need different numbers.
 *
 * So it is measured from the element itself and republished whenever it
 * changes. `ResizeObserver` rather than a resize listener, because the bar can
 * change height without the window doing anything — a longer store name, a role
 * that sees one more section.
 */
export function usePublishedTopbarHeight(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const bar = ref.current;
    if (bar === null) return;

    const publish = () => {
      document.documentElement.style.setProperty(
        '--topbar-height',
        `${bar.getBoundingClientRect().height}px`,
      );
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [ref]);
}
